import type { FindFn } from "@shared/models/analytics/base"
import type { CardStack } from "@shared/models/card-stack"
import type { Course, CourseCalendarDate, Unit } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { User } from "@shared/models/user"
import { compact, find, get, isNil } from "lodash-es"
import { getCourseDateForCardStackId } from "../../unit-utils"

interface Payload {
  cardStackId: string
  planbookId?: string
  planbook?: { id: string }
  courseId?: string | null
  course?: { id: string }
  dateString?: string | null
  websiteId?: string
}

export interface DefaultLessonModels {
  cardStack: CardStack
  cardStackType: CardStack["attributes"]["usedAs"] | undefined
  /** Resolved from the payload first, then the card stack — reliable even when the doc is not. */
  planbookId: string | undefined
  /** Resolved from the payload first, then the card stack — reliable even when the doc is not. */
  courseId: string | null
  course: Course | null
  dateStringForCardStack: string | null
  cardStackIdAndAncestorIds: Array<string>
  courseDate: CourseCalendarDate | null
  planbook: Planbook | undefined
  unit: Unit | null
  user: User | null
}

export default function (payload: Payload, findFn: FindFn, userId: string): DefaultLessonModels {
  // fetching card stack
  let cardStack = (findFn("card-stack", payload.cardStackId) ||
    findFn("card-stack-summary", payload.cardStackId) ||
    findFn("class-website-lesson", payload.cardStackId)) as CardStack
  let cardStackType = get(get(cardStack, "attributes"), "usedAs")

  let course: null | Course = null
  let courseDate: null | CourseCalendarDate = null
  let dateStringForCardStack: null | string = payload.dateString ?? null

  // Payload-first: an explicit `courseId: null` means "no course" (a template edit) and must not
  // fall through to the derivation chain. Only an absent field derives.
  let courseId: string | null =
    payload.courseId !== undefined
      ? payload.courseId
      : get(payload, "course.id") ||
        (get(cardStack, "relationships.course.data.id") as string | undefined) ||
        null
  if (!isNil(courseId)) {
    course = findFn("course", courseId) ?? null
  }
  if (course) {
    courseDate = getCourseDateForCardStackId(course.attributes.calendar.dates, payload.cardStackId)
  }

  if (dateStringForCardStack === null && courseDate && courseDate.type === "course-date-custom") {
    dateStringForCardStack = courseDate.attributes.dateString
  }

  // finding planbook
  let planbookId =
    payload.planbookId ||
    get(payload, "planbook.id") ||
    get(cardStack, "relationships.planbook.data.id")
  // `findFn`'s return type over-promises: an unloaded planbook comes back undefined.
  let planbook: Planbook | undefined = planbookId ? findFn("planbook", planbookId) : undefined

  let cardStackIdAndAncestorIds = compact([
    planbookId,
    payload.websiteId,
    courseId,
    payload.cardStackId,
  ])

  // finding unit
  let unit =
    cardStackType === "unit" && course !== null
      ? find(course.attributes.calendar.units, { id: payload.cardStackId })
      : null

  // Just ensure unit is not undefined
  if (unit === undefined) unit = null
  if (unit !== null) dateStringForCardStack = unit.startDate

  // finding the user
  let user = findFn("user", userId)

  return {
    cardStack,
    cardStackType,
    planbookId,
    courseId,
    course,
    dateStringForCardStack,
    cardStackIdAndAncestorIds,
    courseDate,
    planbook,
    unit,
    user,
  }
}
